import { useCallback, useRef, useState } from "react";
import { GitCommitHorizontal, History, RefreshCw } from "lucide-react";
import { t } from "../../i18n";
import { api } from "../../api";
import type {
  DevelopmentSelection,
  Result,
} from "../chat/ChatDevelopmentPanel.types";
import { selectionBody } from "../chat/ChatDevelopmentPanel.types";

import { useGitRefresh } from "./useGitRefresh";

import { parseGitLog } from "./git-log";
import type { GitLogEntry } from "./git-log";

export type Props = {
  conversationId: string;
  selection: DevelopmentSelection;
  active?: boolean;
};
export function GitLogPanel({
  conversationId,
  selection,
  active = true,
}: Props) {
  const [entries, setEntries] = useState<GitLogEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { workspaceId, repositoryId } = selection;
  const inFlight = useRef(false);
  const load = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    try {
      const result = await api<Result>(
        `/conversations/${conversationId}/development-workspace/git/log`,
        {
          method: "POST",
          notifyDataChange: false,
          body: JSON.stringify(selectionBody({ workspaceId, repositoryId })),
        },
      );
      if (result.code !== 0) {
        setError(
          result.stderr.trim() ||
            result.stdout.trim() ||
            t("Git log could not be loaded"),
        );
        return;
      }
      setEntries(parseGitLog(result.stdout));
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : t("Git log could not be loaded"),
      );
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }, [conversationId, workspaceId, repositoryId]);
  const root = useGitRefresh(load, active);
  return (
    <div ref={root} className="flex h-full min-h-0 flex-col" aria-busy={busy}>
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-stone-200 px-3 dark:border-white/10">
        <History size={15} className="text-stone-500" />
        <span className="text-xs font-semibold">{t("Git log")}</span>
        <span className="text-[11px] text-stone-500">
          {t("Latest 100 commits")}
        </span>
        <button
          className="button ml-auto h-8 p-2"
          title={t("Refresh")}
          aria-label={t("Refresh")}
          disabled={busy}
          onClick={() => void load()}
        >
          <RefreshCw
            size={14}
            className={busy ? "motion-safe:animate-spin" : ""}
          />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {error && (
          <div
            role="alert"
            className="m-3 rounded-lg bg-red-50 p-2 text-xs whitespace-pre-wrap text-red-700 dark:bg-red-950/30 dark:text-red-300"
          >
            {error}
          </div>
        )}
        {entries.map((entry) => (
          <article
            key={`${entry.hash}-${entry.date}`}
            className="border-b border-stone-200 p-3 dark:border-white/10"
          >
            <div className="flex min-w-0 items-start gap-2">
              <GitCommitHorizontal
                size={15}
                className="mt-0.5 shrink-0 text-lumen-600"
              />
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-center gap-2">
                  <span
                    className="truncate text-sm font-semibold"
                    title={entry.subject}
                  >
                    {entry.subject}
                  </span>
                  {entry.refs && (
                    <span className="max-w-48 truncate rounded bg-lumen-100 px-1.5 py-0.5 text-[10px] font-semibold text-lumen-800 dark:bg-lumen-900/40 dark:text-lumen-200">
                      {entry.refs}
                    </span>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-stone-500">
                  <code className="font-mono text-stone-700 dark:text-stone-300">
                    {entry.hash}
                  </code>
                  <span>{entry.author}</span>
                  <time dateTime={entry.date}>
                    {new Date(entry.date).toLocaleString()}
                  </time>
                </div>
              </div>
            </div>
          </article>
        ))}
        {!error && !busy && entries.length === 0 && (
          <div className="grid h-40 place-items-center text-sm text-stone-400">
            {t("No commits yet")}
          </div>
        )}
      </div>
    </div>
  );
}
